import { useState } from 'react'
import { useRouter } from 'next/router'
import { H1, H5, P2, Container, CircleButton, Avatar, Tooltip, Icon } from '@moneymade/moneymade-ui'
import copy from 'copy-to-clipboard'
import PropTypes from 'prop-types'
import VisibilitySensor from 'react-visibility-sensor'

import { ROUTES } from 'static/menusConstants'
import PlatformFavorite from 'components/ui/PlatformFavorite/PlatformFavoriteContainer'
import { getCdnImg } from 'utils/getters'
import styles from './TopBlock.module.scss'

const TopBlock = ({
  review: { name, title, description, cardColor, image },
  handleStickyChange,
  investment: { logo, id, website }
}) => {
  const { back, push } = useRouter()

  const [copySuccess, setCopySuccess] = useState(false)

  const handleClickCopy = url => {
    copy(url)
    setCopySuccess(true)
    setTimeout(() => setCopySuccess(false), 1200)
  }

  const handleShareClick = event => {
    event.stopPropagation()
    if (website) handleClickCopy(website)
  }

  return (
    <section className={styles.TopBlock}>
      <div className={styles.Block}>
        <div className={styles.FloatingBtns}>
          <div className={styles.Left}>
            <CircleButton
              className={styles.BackBtnFloating}
              color="white"
              icon={<Icon className="icon-point-left" size={16} />}
              onClick={() => (window.prevRoute ? back() : push(`/${ROUTES.learnArticle}`))}
            />
          </div>
          <div className={styles.Right}>
            <div className={styles.BtnFavContainer}>
              <Tooltip tooltipText={copySuccess ? 'Copied!' : 'Click to copy profile URL to share'} position="left">
                <CircleButton
                  className={styles.BtnShare}
                  color="white"
                  icon={<i className="icon-share" />}
                  onClick={handleShareClick}
                />
              </Tooltip>

              {id && <PlatformFavorite className={styles.BtnFav} platformId={id} />}
            </div>
          </div>
        </div>

        <VisibilitySensor offset={{ top: -50 }} onChange={handleStickyChange}>
          <Container width={1000}>
            <div className={styles.Header}>
              <Avatar width={32} height={32} src={getCdnImg(logo, 32)} alt="title" className={styles.Logo} />
              <P2>{`${name} Review`}</P2>
            </div>
            <H1 type="heading" weight="bold" className={styles.Title}>
              {title}
            </H1>
          </Container>
        </VisibilitySensor>

        <div style={{ backgroundColor: cardColor }} className={styles.Image}>
          <Container fullWidth>
            <img alt={title} src={getCdnImg(image, 500)} />
          </Container>
        </div>

        <Container width={1000}>
          <H5
            type="heading"
            weight="light"
            nestedStyles
            className={styles.Description}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </Container>
      </div>

      <div style={{ backgroundColor: cardColor }}>
        <Container fullWidth className={styles.ImgBlock}>
          <img alt={title} src={getCdnImg(image, 450)} />
        </Container>
      </div>
    </section>
  )
}

TopBlock.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    cardColor: PropTypes.string,
    logo: PropTypes.string,
    image: PropTypes.string,
    content: PropTypes.string,
    published: PropTypes.string,
    website: PropTypes.string
  }),
  investment: PropTypes.shape({
    logo: PropTypes.string,
    averageRating: PropTypes.number,
    newRisks: PropTypes.arrayOf(PropTypes.string),
    minInvestment: PropTypes.number,
    target: PropTypes.shape({
      min: PropTypes.string,
      max: PropTypes.string
    }),
    fees: PropTypes.number,
    website: PropTypes.string,
    id: PropTypes.string,
    promotion: PropTypes.string,
    investmentOptions: PropTypes.string,
    fundYourInvestmentWith: PropTypes.string,
    youllLikeIf: PropTypes.string,
    appAndroid: PropTypes.bool,
    appIos: PropTypes.bool
  }),
  handleStickyChange: PropTypes.func
}

TopBlock.defaultProps = {
  review: {
    id: '',
    name: '',
    title: '',
    description: '',
    logo: '',
    image: '',
    content: '',
    published: '',
    cardColor: '',
    website: ''
  },
  investment: {
    logo: '',
    averageRating: 0,
    newRisks: [],
    minInvestment: 0,
    target: {
      min: '0',
      max: '0'
    },
    fees: 0,
    website: '',
    id: '',
    promotion: '',
    investmentOptions: '',
    fundYourInvestmentWith: '',
    youllLikeIf: '',
    appAndroid: false,
    appIos: false
  },
  handleStickyChange: () => {}
}

export default TopBlock
